import React from "react";
import { TemplateProps } from "../../types";
import { View, StyleSheet } from "react-native";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { BackgroundGradient } from "../components/BackgroundGradient";
import { ConfigurationComponent, useConfig } from "../providers/ConfigProvider";
import { first } from "lodash";
import { ErrorScreen } from "../screens/ErrorScreen";
import { MediaContentCarosel } from "../components/MediaContentCarosel";
import { TextCards } from "../components/TextCards";
import { PaginatedEvents } from "../components/PaginatedEvents";
import { MultiColumnSchedule } from "../components/MultiColumnSchedule";
import { NutritionMenu } from "../components/NutritionMenu";
import { NutritionDiningStation } from "../components/NutritionDiningStation";

const makeStyles = (theme: ThemeOptions) =>
  StyleSheet.create({
    page: {
      backgroundColor: theme.background,
      flex: 1,
    },
  });

export const Custom = ({ onLoopComplete }: TemplateProps) => {
  const theme = useTheme();
  const styles = makeStyles(theme);
  const config = useConfig();

  const component =
    config.status === "active" ? first(config.components) : undefined;

  if (component == null) {
    return (
      <ErrorScreen
        message="No component found"
        subMessages={[
          "You can add a component to this configuration via the Digital Displays management app.",
        ]}
      />
    );
  }

  return (
    <View style={styles.page}>
      <BackgroundGradient background={theme.background} accent={theme.accent} />
      <Component component={component} onLoopComplete={onLoopComplete} />
    </View>
  );
};

const Component = ({
  component,
  onLoopComplete,
}: {
  component: ConfigurationComponent;
  onLoopComplete: () => void;
}) => {
  if (component.componentType === "media") {
    return (
      <MediaContentCarosel
        data={component.data}
        onLoopComplete={onLoopComplete}
      />
    );
  }

  if (component.componentType === "cards") {
    return <TextCards data={component.data} onLoopComplete={onLoopComplete} />;
  }

  if (component.componentType === "schedule") {
    return (
      <PaginatedEvents
        events={component.data.events}
        timeZone={component.data.timeZone}
        onLoopComplete={onLoopComplete}
      />
    );
  }

  if (
    component.componentType === "multi_column_schedule" ||
    component.componentType === "multi_day_schedule"
  ) {
    return (
      <MultiColumnSchedule
        data={component.data}
        onLoopComplete={onLoopComplete}
      />
    );
  }

  if (component.componentType === "nutrition_menu") {
    return (
      <NutritionMenu data={component.data} onLoopComplete={onLoopComplete} />
    );
  }

  if (component.componentType === "nutrition_dining_station") {
    return (
      <NutritionDiningStation
        data={component.data}
        onLoopComplete={onLoopComplete}
      />
    );
  }

  throw new Error(
    `Custom layout unhandled component: ${JSON.stringify(component)}`,
  );
};
